import {
  Box,
  Card,
  CardBody,
  Circle,
  HStack,
  Progress,
  Skeleton,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { useGetOptionAssetQuery } from '@blockpulse3/graphql/hooks';
import { formatNumberInt, formatNumberPercentage } from '@blockpulse3/helpers';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';

type Props = unknown;

export function OptionSummaryProgression(): JSX.Element {
  const i18nOptionSummaryProgression = useTranslations('OptionSummaryProgression');

  const { assetId = '', companyId = '' } = useParams();

  const { data, loading, error } = useGetOptionAssetQuery({
    variables: {
      getAssetInput: {
        assetId,
        companyId,
      },
    },
  });

  if (loading) {
    return (
      <Skeleton>
        <Card h="200px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard h="200px" />;
  }

  if (!data) {
    return <ErrorQueryCard h="200px" />;
  }

  const option = data.getOptionAsset;
  const optionData = data.getOptionAsset.data;

  return (
    <Card>
      <CardBody as={Stack} spacing="4">
        <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: '4', md: '8' }}>
          <Stat bg="gray.50" p="4" justifyContent="start">
            <StatLabel color="gray.500" fontSize="md" fontWeight="500">
              {i18nOptionSummaryProgression('TotalOptions')}
            </StatLabel>
            <StatNumber>{formatNumberInt(option.initialSupply)}</StatNumber>
          </Stat>
          <Stat bg="gray.50" p="4" justifyContent="start">
            <StatLabel color="gray.500" fontSize="md" fontWeight="500">
              {i18nOptionSummaryProgression('NonGrantedOptions')}
            </StatLabel>
            <StatNumber>{formatNumberInt(option.remainingSupply)}</StatNumber>
          </Stat>
          <Stat bg="gray.50" p="4" justifyContent="start">
            <StatLabel color="gray.500" fontSize="md" fontWeight="500">
              {i18nOptionSummaryProgression('CapitalPercentage')}
            </StatLabel>
            <StatNumber>{formatNumberPercentage(optionData.shareCapitalPercentage)}</StatNumber>
          </Stat>
        </Stack>
        <HStack spacing="3">
          <HStack spacing="2">
            <Circle background="indigo.500" size="6px" />
            <Text>
              {formatNumberPercentage(optionData.exercisedPercentage)}{' '}
              {i18nOptionSummaryProgression('PercentExercised')}
            </Text>
          </HStack>
          <HStack spacing="2">
            <Circle background="indigo.300" size="6px" />
            <Text>
              {formatNumberPercentage(optionData.totalVestedBalancePercentage)}{' '}
              {i18nOptionSummaryProgression('PercentExercisable')}
            </Text>
          </HStack>
          <HStack spacing="2">
            <Circle background="gray.300" size="6px" />
            <Text>
              {formatNumberPercentage(optionData.grantedPercentage)}{' '}
              {i18nOptionSummaryProgression('PercentGranted')}
            </Text>
          </HStack>
        </HStack>
        <Box position="relative" height="12px">
          <Progress
            position="absolute"
            top="0"
            value={optionData.exercisedPercentage}
            rounded="md"
            height="full"
            width="full"
            sx={{ '& > div': { backgroundColor: 'indigo.500' } }}
            bg="transparent"
            zIndex="3"
          />
          <Progress
            position="absolute"
            top="0"
            value={optionData.totalVestedBalancePercentage}
            rounded="md"
            height="full"
            width="full"
            sx={{ '& > div': { backgroundColor: 'indigo.300' } }}
            bg="transparent"
            zIndex="2"
          />
          <Progress
            position="absolute"
            top="0"
            value={optionData.grantedPercentage}
            rounded="md"
            height="full"
            width="full"
            sx={{ '& > div': { backgroundColor: 'gray.300' } }}
            zIndex="1"
          />
        </Box>
      </CardBody>
    </Card>
  );
}

export type OptionSummaryProgressionProps = Props;
